import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useOutletContext } from 'react-router-dom';
import { API } from '../../utils/api';
import ConfirmModal from '../../components/modals/ConfirmModal';
import SuccessModal from '../../components/modals/SuccessModal';
import ErrorModal from '../../components/modals/ErrorModal';
import { GracePeriodBanner } from '../../components/subscription';
import { useSubscription } from '../../context/SubscriptionContext';

const AdminPlansPage = () => {
  const { showGraceBanner, refreshStatus, hasPendingDowngrade, pendingDowngrade, hasFlashUpgrade, flashUpgrade } = useSubscription();
  const { fetchSubscriptionFeatures } = useOutletContext() || {};
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [availablePlans, setAvailablePlans] = useState([]);
  const [billingHistory, setBillingHistory] = useState([]);
  const [subscriptionLog, setSubscriptionLog] = useState([]);
  const [activeHistoryTab, setActiveHistoryTab] = useState('billing'); // 'billing' or 'subscription'
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancellingDowngrade, setCancellingDowngrade] = useState(false);
  const [keepingCurrentPlan, setKeepingCurrentPlan] = useState(false);
  const [cancellingUpgrade, setCancellingUpgrade] = useState(false);
  const [capturingUpgradePayment, setCapturingUpgradePayment] = useState(false);
  const [downloadingDocument, setDownloadingDocument] = useState(null); // Track which document is downloading
  
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, title: '', message: '', onConfirm: () => {} });
  const [keepPlanModal, setKeepPlanModal] = useState({ isOpen: false });
  const [successModal, setSuccessModal] = useState({ isOpen: false, title: '', message: '' });
  const [errorModal, setErrorModal] = useState({ isOpen: false, title: '', message: '' });

  // Feature groups configuration
  const featureGroups = [
    {
      name: 'Organisation Setup',
      features: [
        { key: 'maxSites', label: 'Max. Number of Sites', type: 'value' },
        { key: 'maxTeamMembers', label: 'Max. Number of Team Members', type: 'value' },
        { key: 'storage', label: 'Storage (GB)', type: 'value', suffix: ' GB' },
      ]
    },
    {
      name: 'Core Modules',
      features: [
        { key: 'dashboard', label: 'Dashboard with comprehensive metrics', type: 'boolean' },
        { key: 'siteManagement', label: 'Site Management', type: 'boolean' },
        { key: 'teamManagement', label: 'Team Management', type: 'boolean' },
        { key: 'taskManagement', label: 'Task Management', type: 'boolean' },
        { key: 'photoGallery', label: 'Photo Gallery', type: 'boolean' },
      ]
    },
    {
      name: 'Additional Modules',
      features: [
        { key: 'procurement', label: 'Procurement (Materials)', type: 'boolean' },
        { key: 'expenses', label: 'Expenses', type: 'boolean' },
        { key: 'overtime', label: 'Overtime', type: 'boolean' },
        { key: 'holidaysModule', label: 'Holidays', type: 'boolean' },
      ]
    },
    {
      name: 'Business Features',
      features: [
        { key: 'teamCapacity', label: 'Team Capacity', type: 'boolean' },
        { key: 'teamPerformance', label: 'Team Performance', type: 'boolean' },
        { key: 'workDiary', label: 'Work Diary', type: 'boolean' },
        { key: 'progressReports', label: 'Progress Reports', type: 'boolean' },
        { key: 'quotesAndInvoices', label: 'Quotes & Invoices', type: 'boolean' },
        { key: 'payments', label: 'Payments & CIS Statements', type: 'boolean' },
      ]
    },
    {
      name: 'Mobile App Experience',
      features: [
        { key: 'adminMobileApp', label: 'Admin Mobile App', type: 'boolean' },
        { key: 'offlineMode', label: 'Offline Clock-in / Clock-out', type: 'boolean' },
        { key: 'autoClockOut', label: 'Auto Clock-Out', type: 'boolean' },
        { key: 'pushNotifications', label: 'Push Notifications', type: 'boolean' },
      ]
    },
    {
      name: 'AI Powered Features',
      features: [
        { key: 'neo', label: 'Neo AI', type: 'boolean' },
        { key: 'aiReportGeneration', label: 'AI Report Generation', type: 'boolean' },
        { key: 'neoCreditsMonthly', label: 'Neo Credits (Monthly) - Admin', type: 'value' },
        { key: 'neoCreditsMonthlyTeam', label: 'Neo Credits (Monthly) - Team', type: 'value' },
      ]
    },
  ];

  useEffect(() => {
    fetchData();
    checkForPayPalReturn();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [currentSubResponse, plansResponse, billingResponse, logResponse] = await Promise.all([
        axios.get(`${API}/admin/current-subscription`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }),
        axios.get(`${API}/subscription-packages`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }),
        axios.get(`${API}/admin/billing-history`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }).catch((err) => { 
          console.error('Billing history fetch error:', err);
          return { data: { has_records: false, transactions: [] }};
        }),
        axios.get(`${API}/admin/subscription-log`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }).catch(() => ({ data: { logs: [] }}))
      ]);
      
      setCurrentSubscription(currentSubResponse.data);
      setAvailablePlans(plansResponse.data);
      setBillingHistory(billingResponse.data.transactions || []);
      setSubscriptionLog(logResponse.data.logs || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      setErrorModal({
        isOpen: true,
        title: 'Load Failed',
        message: 'Failed to load subscription data: ' + (error.response?.data?.detail || error.message)
      });
    } finally {
      setLoading(false);
    }
  };

  const checkForPayPalReturn = () => {
    // With BrowserRouter, query params are in window.location.search
    // But we also check hash for backward compatibility with old URLs
    let queryString = window.location.search.slice(1); // Remove leading '?'
    
    // Fallback: check hash for backward compatibility
    if (!queryString && window.location.hash.includes('?')) {
      const hashQueryIndex = window.location.hash.indexOf('?');
      queryString = window.location.hash.substring(hashQueryIndex + 1);
    }
    
    const urlParams = new URLSearchParams(queryString);
    
    const subscriptionId = urlParams.get('subscription_id');
    const baToken = urlParams.get('ba_token');
    const success = urlParams.get('success');
    const cancelled = urlParams.get('cancelled');
    const upgradePayment = urlParams.get('upgrade_payment');
    const keepPlan = urlParams.get('keep_plan');
    const planName = urlParams.get('plan');
    const errorMessage = urlParams.get('message');
    
    // Handle keep plan return
    if (keepPlan === 'success') {
      setSuccessModal({
        isOpen: true,
        title: 'Plan Kept Successfully!',
        message: `You're now staying on the ${planName || 'previous'} plan. Your pending downgrade has been cancelled.`
      });
      fetchData();
      refreshStatus();
      if (fetchSubscriptionFeatures) fetchSubscriptionFeatures();
      window.history.replaceState({}, '', '/admin/plans');
      return;
    }
    
    if (keepPlan === 'cancelled') {
      setErrorModal({
        isOpen: true,
        title: 'Cancelled',
        message: 'You cancelled the plan change. Your pending downgrade is still scheduled.'
      });
      window.history.replaceState({}, '', '/admin/plans');
      return;
    }
    
    if (keepPlan === 'error') {
      setErrorModal({
        isOpen: true,
        title: 'Error',
        message: `Failed to keep your current plan: ${errorMessage || 'Unknown error'}. Please try again.`
      });
      window.history.replaceState({}, '', '/admin/plans');
      return;
    }
    
    // Handle upgrade payment return - payment was captured by backend handler
    if (upgradePayment === 'success') {
      // Backend already captured the payment - show modal with Complete Upgrade button
      // Refresh data first to get the upgrade details
      fetchData();
      refreshStatus();
      
      // Show confirmation modal to proceed directly to step 2
      setConfirmModal({
        isOpen: true,
        title: 'Payment Received!',
        message: 'Your upgrade fee has been captured successfully.\n\n' +
          '⏰ You have 24 hours to complete this upgrade. If not completed, your payment will be automatically refunded.\n\n' +
          'Click "Complete Upgrade" to proceed to PayPal and finalize your subscription change.',
        onConfirm: () => {
          setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: () => {} });
          completeUpgrade();
        },
        onClose: () => setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: () => {} }),
        confirmText: 'Complete Upgrade',
        cancelText: 'Later',
        variant: 'success'
      });
      
      window.history.replaceState({}, '', '/admin/plans');
      return;
    }
    
    if (upgradePayment === 'cancelled') {
      setErrorModal({
        isOpen: true,
        title: 'Upgrade Payment Cancelled',
        message: 'You cancelled the upgrade payment. No charges were made.'
      });
      window.history.replaceState({}, '', '/admin/plans');
      return;
    }
    
    if (upgradePayment === 'error') {
      // Map error codes to user-friendly messages
      const errorMessages = {
        'missing_token': 'The payment session was invalid. Please try again.',
        'order_not_found': 'Could not find your payment order. Please try again.',
        'auth_failed': 'Payment authentication failed. Please try again.',
        'capture_failed': 'Failed to process your payment. Please try again.',
        'payment_incomplete': 'Payment was not completed. Please try again.',
        'server_error': 'A server error occurred. Please try again later.'
      };
      
      setErrorModal({
        isOpen: true,
        title: 'Payment Error',
        message: errorMessages[errorMessage] || 'An error occurred during payment. Please try again.'
      });
      window.history.replaceState({}, '', '/admin/plans');
      return;
    }
    
    if (cancelled === 'true') {
      setErrorModal({
        isOpen: true,
        title: 'Subscription Cancelled',
        message: 'You cancelled the subscription process. No changes were made.'
      });
      window.history.replaceState({}, '', '/admin/plans');
      return;
    }
    
    if ((subscriptionId || baToken) && success === 'true') {
      activateSubscription(subscriptionId || baToken);
      window.history.replaceState({}, '', '/admin/plans');
    }
  };

  const activateSubscription = async (subscriptionId) => {
    try {
      const response = await axios.post(
        `${API}/admin/subscription/activate`,
        { subscription_id: subscriptionId },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }}
      );
      
      // Determine the appropriate message based on the type of activation
      let title = 'Subscription Activated!';
      let message = 'Your subscription has started successfully!';
      
      if (response.data.was_mid_subscription_upgrade) {
        // This was a two-step upgrade completion
        title = 'Upgrade Complete!';
        message = `Your subscription has been upgraded to ${response.data.tier_name || response.data.tier}!`;
      }
      
      if (response.data.enforcement?.enforcement_applied) {
        const enforcement = response.data.enforcement;
        message += `\n\n⚠️ Due to downgrade limits:\n`;
        if (enforcement.sites_deactivated > 0) {
          message += `- ${enforcement.sites_deactivated} site(s) deactivated (new limit: ${enforcement.new_limits.max_sites})\n`;
        }
        if (enforcement.members_deactivated > 0) {
          message += `- ${enforcement.members_deactivated} team member(s) deactivated (new limit: ${enforcement.new_limits.max_members})\n`;
        }
        message += `\nPlease visit Sites and Team to reactivate the ones you need.`;
      }
      
      setSuccessModal({
        isOpen: true,
        title: title,
        message: message
      });
      
      await fetchData();
      await refreshStatus(); // Refresh sidebar subscription state
      if (fetchSubscriptionFeatures) await fetchSubscriptionFeatures(); // Refresh sidebar feature availability
    } catch (error) {
      setErrorModal({
        isOpen: true,
        title: 'Activation Failed',
        message: error.response?.data?.detail || 'Failed to activate subscription'
      });
    }
  };

  // ============================================================================
  // MID-SUBSCRIPTION UPGRADE FUNCTIONS
  // ============================================================================

  // Request deduplication - track in-flight requests
  const [pendingRequestId, setPendingRequestId] = React.useState(null);

  const captureUpgradePayment = async (orderId) => {
    // Prevent duplicate captures
    if (pendingRequestId === `capture-${orderId}`) {
      console.log('Duplicate capture request blocked');
      return;
    }
    
    setPendingRequestId(`capture-${orderId}`);
    setCapturingUpgradePayment(true);
    try {
      const response = await axios.post(
        `${API}/admin/subscription/capture-upgrade-payment`,
        { order_id: orderId },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }}
      );
      
      // Handle idempotent response (already captured)
      if (response.data.already_captured) {
        setSuccessModal({
          isOpen: true,
          title: 'Payment Already Received',
          message: `Your upgrade fee has already been captured.\n\n` +
            `Click the "Complete Upgrade" button to finish upgrading to ${response.data.target_plan}.`
        });
      } else {
        setSuccessModal({
          isOpen: true,
          title: 'Payment Received!',
          message: `Your upgrade fee of £${response.data.amount?.toFixed(2)} has been captured successfully.\n\n` +
            `Click the "Complete Upgrade" button to finish upgrading to ${response.data.target_plan}.\n\n` +
            `⏰ You have ${response.data.timeout_hours} hours to complete this upgrade.`
        });
      }
      
      await fetchData();
      await refreshStatus();
    } catch (error) {
      setErrorModal({
        isOpen: true,
        title: 'Payment Capture Failed',
        message: error.response?.data?.detail || 'Failed to capture upgrade payment'
      });
    } finally {
      setCapturingUpgradePayment(false);
      setPendingRequestId(null);
    }
  };

  const initiateUpgradePayment = async (planTier, planName, planPrice, billingInfo) => {
    // Prevent duplicate initiation
    if (pendingRequestId === `initiate-${planTier}`) {
      console.log('Duplicate initiate request blocked');
      return;
    }
    
    setPendingRequestId(`initiate-${planTier}`);
    setSubscribing(true);
    try {
      const response = await axios.post(
        `${API}/admin/subscription/initiate-upgrade-payment`,
        { target_plan_tier: planTier },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }}
      );
      
      if (response.data.approval_url) {
        // Store info for return
        sessionStorage.setItem('upgradeTargetPlan', planName);
        sessionStorage.setItem('upgradeAmount', response.data.amount);
        
        // Redirect to PayPal for payment
        window.location.href = response.data.approval_url;
      } else {
        setErrorModal({
          isOpen: true,
          title: 'Error',
          message: 'Failed to get PayPal approval URL'
        });
        setPendingRequestId(null);
      }
    } catch (error) {
      setErrorModal({
        isOpen: true,
        title: 'Upgrade Payment Failed',
        message: error.response?.data?.detail || 'Failed to initiate upgrade payment'
      });
      setPendingRequestId(null);
    } finally {
      setSubscribing(false);
    }
  };

  const completeUpgrade = async () => {
    // Prevent duplicate completion requests
    if (pendingRequestId === 'complete-upgrade') {
      console.log('Duplicate complete-upgrade request blocked');
      return;
    }
    
    setPendingRequestId('complete-upgrade');
    setSubscribing(true);
    try {
      const response = await axios.post(
        `${API}/admin/subscription/complete-upgrade`,
        {},
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }}
      );
      
      if (response.data.immediate) {
        // Upgrade applied immediately
        setSuccessModal({
          isOpen: true,
          title: 'Upgrade Complete!',
          message: response.data.message || `Your subscription has been upgraded to ${response.data.new_plan}!`
        });
        
        await fetchData();
        await refreshStatus();
        if (fetchSubscriptionFeatures) await fetchSubscriptionFeatures();
        setPendingRequestId(null);
      } else if (response.data.approval_url) {
        // Redirect to PayPal for subscription approval
        window.location.href = response.data.approval_url;
      } else {
        setErrorModal({
          isOpen: true,
          title: 'Error',
          message: 'Unexpected response from server'
        });
        setPendingRequestId(null);
      }
    } catch (error) {
      setErrorModal({
        isOpen: true,
        title: 'Complete Upgrade Failed',
        message: error.response?.data?.detail || 'Failed to complete upgrade'
      });
      setPendingRequestId(null);
    } finally {
      setSubscribing(false);
    }
  };

  const handleCompleteUpgrade = () => {
    const targetPlan = currentSubscription?.mid_subscription_upgrade?.target_plan_name || 'the new plan';
    const amount = currentSubscription?.mid_subscription_upgrade?.amount_paid || 0;
    
    setConfirmModal({
      isOpen: true,
      title: 'Complete Upgrade?',
      message: `You've already paid the upgrade fee of £${amount.toFixed(2)}.\n\n` +
        `Click confirm to complete your upgrade to ${targetPlan}.\n\n` +
        `You'll be redirected to PayPal to confirm the subscription change.`,
      onConfirm: () => {
        setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: () => {} });
        completeUpgrade();
      },
      onClose: () => setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: () => {} })
    });
  };

  const handleCancelIncompleteUpgrade = () => {
    const targetPlan = currentSubscription?.mid_subscription_upgrade?.target_plan_name || 'the new plan';
    const amount = currentSubscription?.mid_subscription_upgrade?.amount_paid || 0;
    
    setConfirmModal({
      isOpen: true,
      title: 'Cancel Upgrade?',
      message: `Are you sure you want to cancel your upgrade to ${targetPlan}?\n\n` +
        `Your payment of £${amount.toFixed(2)} will be refunded. This usually occurs within 1-2 business days but could take longer depending on your payment method.`,
      onConfirm: async () => {
        setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: () => {} });
        setCancellingUpgrade(true);
        try {
          const response = await axios.post(
            `${API}/admin/subscription/cancel-incomplete-upgrade`,
            {},
            { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }}
          );
          
          setSuccessModal({
            isOpen: true,
            title: 'Upgrade Cancelled',
            message: response.data.message || 'Your upgrade has been cancelled and a refund will be processed.'
          });
          
          await fetchData();
          await refreshStatus();
        } catch (error) {
          setErrorModal({
            isOpen: true,
            title: 'Cancel Failed',
            message: error.response?.data?.detail || 'Failed to cancel upgrade'
          });
        } finally {
          setCancellingUpgrade(false);
        }
      },
      onClose: () => setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: () => {} })
    });
  };

  // ============================================================================
  // END MID-SUBSCRIPTION UPGRADE FUNCTIONS
  // ============================================================================

  const handlePlanChange = async (planTier, planName, planPrice, isDowngrade) => {
    const priceNum = typeof planPrice === 'string' ? parseFloat(planPrice.replace('£', '')) : planPrice;
    const hasActiveSubscription = currentSubscription?.paypal_subscription_id && 
                                   currentSubscription?.paypal_status === 'ACTIVE';
    
    // Check if there's an incomplete upgrade in progress
    if (currentSubscription?.mid_subscription_upgrade?.active) {
      const targetTier = currentSubscription.mid_subscription_upgrade.target_tier;
      const targetPlanName = currentSubscription.mid_subscription_upgrade.target_plan_name;
      
      // If clicking on the same plan as incomplete upgrade, show complete option
      if (planTier.toLowerCase() === targetTier?.toLowerCase()) {
        handleCompleteUpgrade();
        return;
      } else {
        setErrorModal({
          isOpen: true,
          title: 'Incomplete Upgrade In Progress',
          message: `You have an incomplete upgrade to ${targetPlanName}. Please complete or cancel it before selecting a different plan.`
        });
        return;
      }
    }
    
    // Check if a revision is already in progress
    if (currentSubscription?.revision_in_progress) {
      setErrorModal({
        isOpen: true,
        title: 'Plan Change In Progress',
        message: 'A plan change was recently initiated but not completed. This may happen if you closed the PayPal window or navigated away during the approval process.\n\nPlease wait 5 minutes and try again, or contact support if the issue persists.'
      });
      return;
    }
    
    if (hasActiveSubscription) {
      // For upgrades, use the new mid-subscription upgrade flow
      if (!isDowngrade) {
        try {
          setSubscribing(true); // Show loading state while fetching
          const billingResponse = await axios.get(
            `${API}/admin/subscription/billing-info`,
            { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }}
          );
          setSubscribing(false);
          
          const billingInfo = billingResponse.data;
          const currentPriceNum = billingInfo.current_price || 0;
          const daysRemaining = billingInfo.days_remaining || 0;
          const cycleDays = billingInfo.cycle_days || 30;
          const nextBillingDate = billingInfo.next_billing_date || 'your next billing date';
          const priceDifference = priceNum - currentPriceNum;
          const proratedAmount = daysRemaining > 0 ? ((priceDifference * daysRemaining) / cycleDays).toFixed(2) : 0;
          
          setConfirmModal({
            isOpen: true,
            title: `Upgrade to ${planName}?`,
            message: `You're about to upgrade to ${planName} (£${priceNum}/month inc. VAT).\n\n` +
              `Billing adjustment for current cycle:\n` +
              `• Current plan: ${billingInfo.current_plan} (£${currentPriceNum.toFixed(2)}/month)\n` +
              `• Days remaining: ${daysRemaining} of ${cycleDays}\n` +
              `• Price difference: £${priceDifference.toFixed(2)}/month\n` +
              `• Prorated charge: £${proratedAmount} (charged now)\n\n` +
              `Your new monthly rate (£${priceNum}) will start on ${nextBillingDate}.\n\n` +
              `You'll be redirected to PayPal to pay the prorated charge first, then confirm the subscription change.`,
            onConfirm: () => {
              setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: () => {} });
              initiateUpgradePayment(planTier, planName, priceNum, billingInfo);
            },
            onClose: () => setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: () => {} })
          });
        } catch (error) {
          setSubscribing(false);
          // Fallback to simple confirmation if billing info fetch fails
          setConfirmModal({
            isOpen: true,
            title: `Upgrade to ${planName}?`,
            message: `You're about to upgrade to ${planName} (£${priceNum}/month inc. VAT). A prorated charge will apply for the remainder of your current billing cycle.\n\n` +
              `You'll be redirected to PayPal to pay the prorated charge first, then confirm the subscription change.\n\n` +
              `Do you want to proceed?`,
            onConfirm: () => {
              setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: () => {} });
              initiateUpgradePayment(planTier, planName, priceNum, {});
            },
            onClose: () => setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: () => {} })
          });
        }
      } else {
        // Downgrade flow - check for pending downgrade first
        const isPendingDowngrade = hasPendingDowngrade || currentSubscription?.pending_downgrade?.active;
        
        if (isPendingDowngrade) {
          setErrorModal({
            isOpen: true,
            title: 'Downgrade Already Pending',
            message: 'You already have a pending downgrade. Please cancel it first if you want to choose a different plan.'
          });
          return;
        }
        
        // Fetch billing info for downgrade message
        try {
          const billingResponse = await axios.get(
            `${API}/admin/subscription/billing-info`,
            { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }}
          );
          
          const billingInfo = billingResponse.data;
          const nextBillingDate = billingInfo.next_billing_date || 'the end of your billing cycle';
          
          setConfirmModal({
            isOpen: true,
            title: `Downgrade to ${planName}?`,
            message: `You're about to downgrade to ${planName} (£${priceNum}/month inc. VAT).\n\n` +
              `Your current ${billingInfo.current_plan} plan will remain active until ${nextBillingDate}.\n\n` +
              `No billing adjustments will be made. After this date, some features may become unavailable.\n\n` +
              `Do you want to proceed?`,
            onConfirm: () => {
              setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: () => {} });
              reviseSubscription(planTier);
            },
            onClose: () => setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: () => {} })
          });
        } catch (error) {
          // Fallback message
          setConfirmModal({
            isOpen: true,
            title: `Downgrade to ${planName}?`,
            message: `You're about to downgrade to ${planName} (£${priceNum}/month inc. VAT).\n\n` +
              `Your current plan will remain active until the end of your billing cycle. ` +
              `After that, some features may become unavailable.\n\n` +
              `Do you want to proceed?`,
            onConfirm: () => {
              setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: () => {} });
              reviseSubscription(planTier);
            },
            onClose: () => setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: () => {} })
          });
        }
      }
    } else {
      // New subscription flow
      setConfirmModal({
        isOpen: true,
        title: `Subscribe to ${planName}?`,
        message: `You're about to subscribe to ${planName} (£${priceNum}/month inc. VAT). Your subscription will begin immediately. Do you want to proceed?`,
        onConfirm: () => {
          setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: () => {} });
          initiateSubscription(planTier);
        },
        onClose: () => setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: () => {} })
      });
    }
  };

  const handleSubscribe = (planTier, planName, planPrice) => {
    handlePlanChange(planTier, planName, planPrice, false);
  };

  const reviseSubscription = async (planTier) => {
    setSubscribing(true);
    try {
      const response = await axios.post(
        `${API}/admin/subscription/revise`,
        { new_plan_tier: planTier },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }}
      );
      
      if (response.data.immediate) {
        // Plan change applied immediately without approval needed
        let successMessage = `Your subscription has been changed to ${response.data.new_plan}.`;
        
        // Add prorated charge info for upgrades
        if (response.data.is_upgrade && response.data.prorated_amount) {
          successMessage += `\n\nA prorated charge of £${response.data.prorated_amount} has been applied.`;
        }
        
        setSuccessModal({
          isOpen: true,
          title: response.data.is_upgrade ? 'Upgrade Complete!' : 'Plan Changed!',
          message: successMessage
        });
        await fetchData();
        await refreshStatus(); // Refresh sidebar subscription state
        if (fetchSubscriptionFeatures) await fetchSubscriptionFeatures(); // Refresh sidebar feature availability
        setSubscribing(false);
      } else {
        // Redirect to PayPal for approval
        window.location.href = response.data.approval_url;
      }
    } catch (error) {
      setSubscribing(false);
      setErrorModal({
        isOpen: true,
        title: 'Plan Change Failed',
        message: error.response?.data?.detail || 'Failed to change subscription plan'
      });
    }
  };

  const initiateSubscription = async (planTier) => {
    setSubscribing(true);
    try {
      const response = await axios.post(
        `${API}/admin/subscribe/${planTier}`,
        {},
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }}
      );
      window.location.href = response.data.approval_url;
    } catch (error) {
      setSubscribing(false);
      setErrorModal({
        isOpen: true,
        title: 'Subscription Failed',
        message: error.response?.data?.detail || 'Failed to create subscription'
      });
    }
  };

  const handleCancelSubscription = () => {
    const billingEndDate = currentSubscription?.next_billing_date 
      ? formatDate(currentSubscription.next_billing_date)
      : 'the end of your current billing period';
    
    setConfirmModal({
      isOpen: true,
      title: 'Cancel Subscription?',
      message: `Are you sure you want to cancel your subscription? You will retain access until ${billingEndDate}, after which your account will be downgraded to the free tier.`,
      onConfirm: () => {
        setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: () => {} });
        cancelSubscription();
      },
      onClose: () => setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: () => {} })
    });
  };

  const cancelSubscription = async () => {
    setCancelling(true);
    try {
      const response = await axios.post(
        `${API}/admin/subscription/cancel`,
        {},
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }}
      );
      
      setSuccessModal({
        isOpen: true,
        title: 'Subscription Cancelled',
        message: response.data.message || 'Your subscription has been cancelled. You will retain access until the end of your current billing period.'
      });
      
      await fetchData();
      await refreshStatus(); // Refresh sidebar subscription state
      if (fetchSubscriptionFeatures) await fetchSubscriptionFeatures(); // Refresh sidebar feature availability
    } catch (error) {
      setErrorModal({
        isOpen: true,
        title: 'Cancellation Failed',
        message: error.response?.data?.detail || 'Failed to cancel subscription'
      });
    } finally {
      setCancelling(false);
    }
  };

  const handleCancelPendingDowngrade = () => {
    const downgradeInfo = pendingDowngrade || currentSubscription?.pending_downgrade;
    if (!downgradeInfo) return;
    
    const effectiveDateDisplay = downgradeInfo.effective_date 
      ? formatDate(downgradeInfo.effective_date)
      : 'the end of your billing cycle';
    
    setConfirmModal({
      isOpen: true,
      title: 'Apply Downgrade Now?',
      message: `This will apply your downgrade to ${downgradeInfo.to_plan || 'the lower plan'} immediately instead of waiting until ${effectiveDateDisplay}.\n\n⚠️ What happens next:\n• You will be moved to the ${downgradeInfo.to_plan || 'lower'} plan right away\n• Any sites/team members over your new plan limits will be deactivated\n• You can reactivate them later up to your new plan's limits\n\nAre you sure you want to proceed?`,
      onConfirm: () => {
        setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: () => {} });
        cancelPendingDowngrade();
      },
      onClose: () => setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: () => {} })
    });
  };

  const cancelPendingDowngrade = async () => {
    setCancellingDowngrade(true);
    try {
      const response = await axios.post(
        `${API}/admin/subscription/cancel-pending-downgrade`,
        {},
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }}
      );
      
      setSuccessModal({
        isOpen: true,
        title: 'Downgrade Applied',
        message: response.data.message || `Your plan has been changed to ${response.data.new_plan}.`
      });
      
      await fetchData();
      await refreshStatus();
      if (fetchSubscriptionFeatures) await fetchSubscriptionFeatures();
    } catch (error) {
      setErrorModal({
        isOpen: true,
        title: 'Failed to Apply Downgrade',
        message: error.response?.data?.detail || 'Failed to apply downgrade'
      });
    } finally {
      setCancellingDowngrade(false);
    }
  };

  const handleKeepCurrentPlan = () => {
    const downgradeInfo = pendingDowngrade || currentSubscription?.pending_downgrade;
    if (!downgradeInfo) return;
    
    setKeepPlanModal({ isOpen: true });
  };

  const keepCurrentPlan = async () => {
    setKeepingCurrentPlan(true);
    setKeepPlanModal({ isOpen: false });
    
    try {
      const response = await axios.post(
        `${API}/admin/subscription/keep-current-plan`,
        {},
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }}
      );
      
      if (response.data.requires_approval && response.data.approval_url) {
        // Redirect to PayPal for approval
        window.location.href = response.data.approval_url;
      } else {
        // No approval needed - plan kept immediately
        setSuccessModal({
          isOpen: true,
          title: 'Plan Kept Successfully!',
          message: response.data.message || `You're now staying on your current plan.`
        });
        
        await fetchData();
        await refreshStatus();
        if (fetchSubscriptionFeatures) await fetchSubscriptionFeatures();
      }
    } catch (error) {
      setErrorModal({
        isOpen: true,
        title: 'Failed to Keep Plan',
        message: error.response?.data?.detail || 'Failed to keep current plan'
      });
    } finally {
      setKeepingCurrentPlan(false);
    }
  };

  const calculateTrialDaysRemaining = () => {
    if (!currentSubscription?.is_trial || !currentSubscription?.trial_end_date) return 0;
    const endDate = new Date(currentSubscription.trial_end_date);
    const now = new Date();
    const daysRemaining = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24));
    return Math.max(0, daysRemaining);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const getFeatureValue = (plan, feature) => {
    const value = plan.features?.[feature.key];
    
    if (feature.type === 'boolean') {
      return value === true;
    }
    
    // For value types, return the actual value
    return value;
  };

  const renderFeatureCell = (plan, feature) => {
    const value = getFeatureValue(plan, feature);
    
    if (feature.type === 'boolean') {
      return value ? (
        <svg className="w-4 h-4 sm:w-5 sm:h-5 text-green-400 mx-auto" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
        </svg>
      ) : (
        <svg className="w-4 h-4 sm:w-5 sm:h-5 text-slate-600 mx-auto" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
      );
    }
    
    // Value type
    const displayValue = value || '0';
    const suffix = feature.suffix || '';
    return (
      <span className="text-white font-medium text-xs sm:text-sm">
        {displayValue}{suffix}
      </span>
    );
  };

  const getPlanPrice = (plan) => {
    const price = plan.price;
    if (typeof price === 'string') {
      return parseFloat(price.replace('£', '')) || 0;
    }
    return price || 0;
  };

  // Check if there's an active PayPal subscription (used for button labels and column highlights)
  const hasActivePayPalSubscription = currentSubscription?.paypal_subscription_id && 
                                      currentSubscription?.paypal_status === 'ACTIVE';

  const getPlanButton = (plan) => {
    const planId = plan.id || plan.tier_key || plan.tier;
    const currentPackageId = currentSubscription?.current_package?.id || 
                            currentSubscription?.current_package?.tier_key || 
                            currentSubscription?.current_package?.tier || null;
    const isCurrentPlan = planId === currentPackageId;
    
    // Check if this plan is the flash upgrade plan (Pro during flash upgrade)
    const isFlashUpgradeActive = hasFlashUpgrade || currentSubscription?.flash_upgrade?.active;
    const isProPlan = (plan.tier_key || plan.id || '').toLowerCase().includes('pro');
    const isFlashUpgradePlan = isFlashUpgradeActive && isProPlan;
    
    // Check if there's an incomplete upgrade
    const hasIncompleteUpgrade = currentSubscription?.mid_subscription_upgrade?.active;
    const incompleteUpgradeTargetTier = currentSubscription?.mid_subscription_upgrade?.target_tier;
    const isIncompleteUpgradeTargetPlan = hasIncompleteUpgrade && 
                                          (planId?.toLowerCase() === incompleteUpgradeTargetTier?.toLowerCase() ||
                                           plan.tier_key?.toLowerCase() === incompleteUpgradeTargetTier?.toLowerCase());
    
    // Check if there's a pending downgrade to this plan
    const isPendingDowngradeActive = hasPendingDowngrade || currentSubscription?.pending_downgrade?.active;
    const pendingDowngradePlanId = currentSubscription?.pending_downgrade?.to_plan_tier || 
                                   pendingDowngrade?.to_plan_tier;
    const isDowngradePendingPlan = isPendingDowngradeActive && 
                                   (planId === pendingDowngradePlanId || 
                                    plan.name === currentSubscription?.pending_downgrade?.to_plan ||
                                    plan.name === pendingDowngrade?.to_plan);
    
    const planPrice = getPlanPrice(plan);
    const hasCurrentPackage = !!currentSubscription?.current_package;
    const currentPrice = hasCurrentPackage ? getPlanPrice(currentSubscription.current_package) : 0;
    
    // Only show Upgrade/Downgrade if user has an active PayPal subscription
    const isUpgrade = hasActivePayPalSubscription && hasCurrentPackage && planPrice > currentPrice;
    const isDowngrade = hasActivePayPalSubscription && hasCurrentPackage && planPrice < currentPrice;
    
    // Handle incomplete upgrade states
    if (hasIncompleteUpgrade) {
      if (isIncompleteUpgradeTargetPlan) {
        // This is the target plan - show "Complete Upgrade" button
        return (
          <div className="flex flex-col items-center gap-1 sm:gap-2">
            <span className="inline-block px-2 sm:px-3 py-1 sm:py-1.5 bg-blue-500/20 text-blue-400 border border-blue-500/50 rounded-lg text-[10px] sm:text-xs font-semibold">
              Payment Received
            </span>
            <button
              onClick={handleCompleteUpgrade}
              disabled={subscribing || capturingUpgradePayment}
              className="px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg text-[10px] sm:text-xs font-semibold transition-all disabled:opacity-50 bg-blue-600 text-white hover:bg-blue-500"
            >
              {subscribing ? '...' : 'Complete Upgrade'}
            </button>
          </div>
        );
      } else {
        // Other plans should be disabled
        return (
          <span className="inline-block px-2 sm:px-3 py-1 sm:py-1.5 bg-slate-700 text-gray-500 rounded-lg text-[10px] sm:text-xs font-semibold cursor-not-allowed" title="Complete or cancel current upgrade first">
            {isCurrentPlan ? 'Current Plan' : isUpgrade ? 'Upgrade' : isDowngrade ? 'Downgrade' : 'Subscribe'}
          </span>
        );
      }
    }
    
    // During flash upgrade: show "Your Plan" badge on actual plan
    if (isFlashUpgradeActive && isCurrentPlan) {
      return (
        <span className="inline-block px-2 sm:px-3 py-1 sm:py-1.5 bg-slate-600 text-white rounded-lg text-[10px] sm:text-xs font-semibold">
          Your Plan
        </span>
      );
    }
    
    // During flash upgrade: show "Flash Upgrade" badge + Upgrade button on Pro
    if (isFlashUpgradePlan) {
      return (
        <div className="flex flex-col items-center gap-1 sm:gap-2">
          <span className="inline-block px-2 sm:px-3 py-1 sm:py-1.5 bg-amber-500 text-white rounded-lg text-[10px] sm:text-xs font-semibold">
            Flash Upgrade
          </span>
          <button
            onClick={() => handlePlanChange(planId, plan.name, plan.price, false)}
            disabled={subscribing}
            className="px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg text-[10px] sm:text-xs font-semibold transition-all disabled:opacity-50 bg-gradient-to-r from-purple-600 to-indigo-600 text-white hover:from-purple-500 hover:to-indigo-500"
          >
            {subscribing ? '...' : 'Upgrade'}
          </button>
        </div>
      );
    }
    
    // Show "Downgrade Pending" badge on the plan being downgraded to
    if (isDowngradePendingPlan) {
      return (
        <span className="inline-block px-2 sm:px-3 py-1 sm:py-1.5 bg-amber-500/20 text-amber-400 border border-amber-500/50 rounded-lg text-[10px] sm:text-xs font-semibold">
          Downgrade Pending
        </span>
      );
    }
    
    // Only show "Current Plan" badge if there's an active PayPal subscription
    // During grace period, user should see "Subscribe" button instead
    if (isCurrentPlan && hasActivePayPalSubscription) {
      return (
        <span className="inline-block px-2 sm:px-3 py-1 sm:py-1.5 bg-purple-500/20 text-purple-400 rounded-lg text-[10px] sm:text-xs font-semibold">
          Current Plan
        </span>
      );
    }
    
    const buttonLabel = isUpgrade ? 'Upgrade' : isDowngrade ? 'Downgrade' : 'Subscribe';
    
    return (
      <button
        onClick={() => handlePlanChange(planId, plan.name, plan.price, isDowngrade)}
        disabled={subscribing}
        className="px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg text-[10px] sm:text-xs font-semibold transition-all disabled:opacity-50 bg-gradient-to-r from-purple-600 to-indigo-600 text-white hover:from-purple-500 hover:to-indigo-500"
      >
        {subscribing ? '...' : buttonLabel}
      </button>
    );
  };

  // Shared modal rendering function to avoid duplicates
  const renderModals = () => {
    const downgradeInfo = pendingDowngrade || currentSubscription?.pending_downgrade;
    const currentPlan = downgradeInfo?.from_plan || 'current';
    const effectiveDate = formatDate(downgradeInfo?.effective_date);
    
    return (
      <>
        <ConfirmModal
          isOpen={confirmModal.isOpen}
          onClose={confirmModal.onClose || (() => setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: () => {} }))}
          onConfirm={confirmModal.onConfirm}
          title={confirmModal.title}
          message={confirmModal.message}
          confirmText={confirmModal.confirmText || "Proceed"}
          cancelText={confirmModal.cancelText || "Cancel"}
          variant={confirmModal.variant || "info"}
        />
        
        {/* Keep Current Plan Modal */}
        {keepPlanModal.isOpen && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-slate-800 rounded-2xl border border-slate-700 max-w-lg w-full p-6 shadow-2xl">
              <h3 className="text-xl font-bold text-white mb-4">Keep {currentPlan} Plan?</h3>
              
              <p className="text-gray-300 mb-4">
                This will cancel your pending downgrade and keep you on the <strong className="text-purple-400">{currentPlan}</strong> plan.
              </p>
              
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 mb-4">
                <p className="text-amber-300 font-medium mb-2">⚠️ Important:</p>
                <ul className="text-amber-200/80 text-sm space-y-1 list-disc list-inside">
                  <li>The change takes effect immediately</li>
                  <li>You will forfeit the remainder of your current billing cycle</li>
                  <li>Your next billing date will reset to today</li>
                </ul>
              </div>
              
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 mb-6">
                <p className="text-blue-300 font-medium mb-2">💡 Tip:</p>
                <p className="text-blue-200/80 text-sm">
                  Since you have full {currentPlan} access until <strong>{effectiveDate}</strong>, you could wait until then to make this change. 
                  We&apos;ll send you a reminder email 48 hours before your downgrade takes effect.
                </p>
              </div>
              
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setKeepPlanModal({ isOpen: false })}
                  className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-gray-300 rounded-lg font-medium transition-colors"
                >
                  Wait for Reminder
                </button>
                <button
                  onClick={keepCurrentPlan}
                  disabled={keepingCurrentPlan}
                  className="px-4 py-2 bg-purple-600 hover:bg-purple-500 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
                >
                  {keepingCurrentPlan ? 'Processing...' : `Keep ${currentPlan} Now`}
                </button>
              </div>
            </div>
          </div>
        )}
        
        <SuccessModal
          isOpen={successModal.isOpen}
          onClose={() => {
            setSuccessModal({ isOpen: false, title: '', message: '' });
          }}
          title={successModal.title}
          message={successModal.message}
        />
        
        <ErrorModal
          isOpen={errorModal.isOpen}
          onClose={() => setErrorModal({ isOpen: false, title: '', message: '' })}
          title={errorModal.title}
          message={errorModal.message}
        />
      </>
    );
  };

  if (loading) {
    return (
      <>
        <div className="flex items-center justify-center p-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
          <span className="ml-3 text-gray-400">Loading subscription data...</span>
        </div>
        {renderModals()}
      </>
    );
  }

  const trialDaysRemaining = calculateTrialDaysRemaining();
  const numPlans = availablePlans.length;
  // Calculate column widths: feature column gets 40%, remaining 60% split equally among plans
  const featureColWidth = 40;
  const planColWidth = 60 / numPlans;

  return (
    <div className="space-y-6">
      {/* Grace Period Banner */}
      {showGraceBanner && <GracePeriodBanner hideSubscribeButton />}

      {/* Combined Flash Upgrade + Pending Downgrade Banner */}
      {(hasFlashUpgrade || currentSubscription?.flash_upgrade?.active) && 
       (hasPendingDowngrade || currentSubscription?.pending_downgrade?.active) && (
        <div className="bg-gradient-to-r from-purple-900/40 via-yellow-900/30 to-amber-900/40 border border-purple-500/50 rounded-xl p-4" data-testid="combined-status-banner">
          <div className="flex flex-col gap-4">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                <svg className="w-5 h-5 text-purple-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-purple-300 font-semibold flex items-center gap-2">
                  <span>Your Subscription Status</span>
                  <span className="px-2 py-0.5 bg-yellow-500/20 text-yellow-400 text-xs rounded-full">Pro Trial Active</span>
                  <span className="px-2 py-0.5 bg-amber-500/20 text-amber-400 text-xs rounded-full">Downgrade Scheduled</span>
                </h3>
                
                {/* Current Status */}
                <div className="mt-3 p-3 bg-yellow-900/30 rounded-lg border border-yellow-500/30">
                  <div className="flex items-center gap-2 text-yellow-300 font-medium text-sm">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
                    </svg>
                    Now: Flash Upgrade (Pro Trial)
                  </div>
                  <p className="text-yellow-200/80 text-sm mt-1">
                    You have <strong className="text-yellow-100">Pro</strong> access until{' '}
                    <strong className="text-yellow-100">
                      {flashUpgrade?.expires_at_display || currentSubscription?.flash_upgrade?.expires_at_display || 'soon'}
                    </strong>
                    {' '}({(flashUpgrade?.days_remaining || currentSubscription?.flash_upgrade?.days_remaining || 0)} days remaining)
                  </p>
                </div>

                {/* What happens next */}
                <div className="mt-2 p-3 bg-amber-900/30 rounded-lg border border-amber-500/30">
                  <div className="flex items-center gap-2 text-amber-300 font-medium text-sm">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    After Flash Upgrade Expires
                  </div>
                  <p className="text-amber-200/80 text-sm mt-1">
                    Your plan will revert to{' '}
                    <strong className="text-amber-100">
                      {flashUpgrade?.original_tier || currentSubscription?.flash_upgrade?.original_tier || 'your original plan'}
                    </strong>, then change to{' '}
                    <strong className="text-amber-100">
                      {pendingDowngrade?.to_plan || currentSubscription?.pending_downgrade?.to_plan}
                    </strong>{' '}on{' '}
                    <strong className="text-amber-100">
                      {formatDate(pendingDowngrade?.effective_date || currentSubscription?.pending_downgrade?.effective_date)}
                    </strong>
                  </p>
                </div>

                {/* Recommendation */}
                <div className="mt-3 flex flex-col sm:flex-row items-start sm:items-center gap-3">
                  <p className="text-purple-200/70 text-xs">
                    <strong className="text-purple-200">Tip:</strong> You can cancel the pending downgrade and keep your current plan.
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={handleKeepCurrentPlan}
                      disabled={keepingCurrentPlan}
                      className="flex-shrink-0 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white px-6 py-3 rounded-lg text-base font-semibold transition-all disabled:opacity-50"
                    >
                      {keepingCurrentPlan ? 'Processing...' : <>Cancel downgrade and Keep Current Plan: <span className="font-bold">{pendingDowngrade?.from_plan || 'Plan'}</span></>}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Flash Upgrade Banner (only when no pending downgrade) */}
      {(hasFlashUpgrade || currentSubscription?.flash_upgrade?.active) && 
       !(hasPendingDowngrade || currentSubscription?.pending_downgrade?.active) && (
        <div className="bg-gradient-to-r from-yellow-900/40 to-orange-900/40 border border-yellow-500/50 rounded-xl p-4" data-testid="flash-upgrade-banner">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-yellow-500/20 flex items-center justify-center">
                <svg className="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
                </svg>
              </div>
              <div>
                <h3 className="text-yellow-300 font-semibold flex items-center gap-2">
                  <span>Flash Upgrade Active</span>
                  <span className="px-2 py-0.5 bg-yellow-500/20 text-yellow-400 text-xs rounded-full">Pro Trial</span>
                </h3>
                <p className="text-yellow-200/80 text-sm mt-1">
                  You have temporary access to all <strong className="text-yellow-100">Pro</strong> features.
                  {' '}Expires on{' '}
                  <strong className="text-yellow-100">
                    {flashUpgrade?.expires_at_display || currentSubscription?.flash_upgrade?.expires_at_display || 'soon'}
                  </strong>.
                </p>
                <p className="text-yellow-200/60 text-xs mt-2">
                  {(flashUpgrade?.days_remaining || currentSubscription?.flash_upgrade?.days_remaining || 0)} days remaining • 
                  Original plan: {flashUpgrade?.original_tier || currentSubscription?.flash_upgrade?.original_tier || 'Starter'}
                </p>
              </div>
            </div>
            <div className="flex-shrink-0 bg-yellow-500/20 border border-yellow-500/50 text-yellow-300 px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2">
              Upgrade to Pro
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
              </svg>
            </div>
          </div>
        </div>
      )}

      {/* Pending Downgrade Banner (only when no flash upgrade) */}
      {(hasPendingDowngrade || currentSubscription?.pending_downgrade?.active) && 
       !(hasFlashUpgrade || currentSubscription?.flash_upgrade?.active) && (
        <div className="bg-amber-900/30 border border-amber-500/50 rounded-xl p-4" data-testid="pending-downgrade-banner">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
                <svg className="w-5 h-5 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div>
                <h3 className="text-amber-300 font-semibold">Pending Downgrade</h3>
                <p className="text-amber-200/80 text-sm mt-1">
                  Your plan will change from{' '}
                  <strong className="text-amber-100">
                    {pendingDowngrade?.from_plan || currentSubscription?.pending_downgrade?.from_plan}
                  </strong>{' '}
                  to{' '}
                  <strong className="text-amber-100">
                    {pendingDowngrade?.to_plan || currentSubscription?.pending_downgrade?.to_plan}
                  </strong>{' '}
                  on{' '}
                  <strong className="text-amber-100">
                    {formatDate(pendingDowngrade?.effective_date || currentSubscription?.pending_downgrade?.effective_date)}
                  </strong>.
                </p>
                <p className="text-amber-200/60 text-xs mt-2">
                  You retain full access to {pendingDowngrade?.from_plan || currentSubscription?.pending_downgrade?.from_plan} features until then.
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleKeepCurrentPlan}
                disabled={keepingCurrentPlan}
                className="flex-shrink-0 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white px-6 py-3 rounded-lg text-base font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {keepingCurrentPlan ? 'Processing...' : <>Cancel downgrade and Keep Current Plan: <span className="font-bold">{pendingDowngrade?.from_plan || currentSubscription?.pending_downgrade?.from_plan || 'Plan'}</span></>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Incomplete Upgrade Banner */}
      {currentSubscription?.mid_subscription_upgrade?.active && (
        <div className="bg-gradient-to-r from-blue-900/40 to-cyan-900/40 border border-blue-500/50 rounded-xl p-4" data-testid="incomplete-upgrade-banner">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                <svg className="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <h3 className="text-blue-300 font-semibold flex items-center gap-2">
                  <span>Incomplete Upgrade</span>
                  <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 text-xs rounded-full">
                    £{currentSubscription.mid_subscription_upgrade.amount_paid?.toFixed(2)} paid
                  </span>
                </h3>
                <p className="text-blue-200/80 text-sm mt-1">
                  You&apos;ve paid the upgrade fee to{' '}
                  <strong className="text-blue-100">
                    {currentSubscription.mid_subscription_upgrade.target_plan_name}
                  </strong>.
                  {' '}Click the button below to complete your upgrade.
                </p>
                <p className="text-blue-200/60 text-xs mt-2 flex items-center gap-1">
                  <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  This upgrade will expire in{' '}
                  <strong className="text-blue-100">
                    {Math.round(currentSubscription.mid_subscription_upgrade.hours_remaining || 0)} hours
                  </strong>.
                  {' '}If not completed, your payment will be automatically refunded.
                </p>
              </div>
            </div>
            <div className="flex gap-2 flex-shrink-0">
              <button
                onClick={handleCompleteUpgrade}
                disabled={subscribing || capturingUpgradePayment}
                className="bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {subscribing ? 'Processing...' : 'Complete Upgrade'}
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
              <button
                onClick={handleCancelIncompleteUpgrade}
                disabled={cancellingUpgrade || subscribing}
                className="bg-slate-700 hover:bg-slate-600 text-gray-300 px-3 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {cancellingUpgrade ? 'Cancelling...' : 'Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 via-purple-700 to-indigo-700 rounded-xl p-6 text-white shadow-lg">
        <h1 className="text-2xl sm:text-3xl font-bold mb-2">Subscription Plans</h1>
        <p className="text-purple-200">Compare plans and choose what works best for your team</p>
      </div>

      {/* Current Subscription Status */}
      {currentSubscription && currentSubscription.current_package && (
        <div className="bg-slate-800 rounded-xl border border-slate-700 p-4">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg bg-purple-600 flex items-center justify-center">
                <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a1 1 0 01-1.707.707L10 12.414l-4.293 4.293A1 1 0 014 16V4z" clipRule="evenodd" />
                </svg>
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="text-lg font-semibold text-white">
                    {currentSubscription.current_package?.name || 'Unknown Plan'}
                  </h3>
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                    currentSubscription.is_trial
                      ? 'bg-green-500/20 text-green-400'
                      : currentSubscription.subscription_status === 'ACTIVE'
                      ? 'bg-purple-500/20 text-purple-400'
                      : currentSubscription.subscription_status === 'CANCELLED'
                      ? 'bg-red-500/20 text-red-400'
                      : currentSubscription.subscription_status === 'SUSPENDED'
                      ? 'bg-amber-500/20 text-amber-400'
                      : currentSubscription.subscription_status === 'EXPIRED'
                      ? 'bg-gray-500/20 text-gray-400'
                      : 'bg-slate-700 text-gray-400'
                  }`}>
                    {currentSubscription.is_trial 
                      ? `Trial · ${trialDaysRemaining} days left` 
                      : currentSubscription.subscription_status || 'Active'}
                  </span>
                </div>
                <p className="text-sm text-gray-400">
                  {(() => {
                    const price = currentSubscription.current_package?.price;
                    const numericPrice = typeof price === 'string' 
                      ? parseFloat(price.toString().replace('£', '')) 
                      : price;
                    return numericPrice > 0 
                      ? `£${numericPrice}/month (inc. VAT)`
                      : 'Free';
                  })()}
                  {currentSubscription.is_trial && currentSubscription.trial_end_date && (
                    <span className="ml-2">· First charge {formatDate(currentSubscription.trial_end_date)}</span>
                  )}
                  {!currentSubscription.is_trial && (() => {
                    const status = currentSubscription.paypal_status || currentSubscription.subscription_status;
                    
                    // For SUSPENDED: show "Billing paused · Resumes {date}"
                    if (status === 'SUSPENDED' && currentSubscription.next_billing_date) {
                      return (
                        <span className="ml-2">
                          · Billing paused · Resumes {formatDate(currentSubscription.next_billing_date)}
                        </span>
                      );
                    }
                    
                    // For CANCELLED/EXPIRED: show last payment date
                    if (status === 'CANCELLED' || status === 'EXPIRED') {
                      const lastDate = currentSubscription.final_payment_date || currentSubscription.last_payment_date;
                      if (lastDate) {
                        return (
                          <span className="ml-2">
                            · Last payment {formatDate(lastDate)}
                          </span>
                        );
                      }
                      return null;
                    }
                    
                    // For ACTIVE or other statuses: show next billing date
                    if (currentSubscription.next_billing_date) {
                      const billingDate = new Date(currentSubscription.next_billing_date);
                      const now = new Date();
                      const isPast = billingDate < now;
                      return (
                        <span className="ml-2">
                          · {isPast ? 'Billing cycle ended' : 'Next billing'} {formatDate(currentSubscription.next_billing_date)}
                        </span>
                      );
                    }
                    
                    return null;
                  })()}
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              {currentSubscription.subscription_status === 'CANCELLED' && (
                <span className="text-xs text-amber-400 bg-amber-500/20 px-3 py-1.5 rounded-lg">
                  Access until {currentSubscription.next_billing_date ? formatDate(currentSubscription.next_billing_date) : 'end of billing period'}
                </span>
              )}
              {currentSubscription.subscription_status === 'SUSPENDED' && (
                <span className="text-xs text-amber-400 bg-amber-500/20 px-3 py-1.5 rounded-lg">
                  Payment issue - please update payment method
                </span>
              )}
              {currentSubscription.subscription_status === 'EXPIRED' && (
                <span className="text-xs text-gray-400 bg-gray-500/20 px-3 py-1.5 rounded-lg">
                  Subscription expired - please renew
                </span>
              )}
              {currentSubscription.paypal_subscription_id && currentSubscription.subscription_status !== 'CANCELLED' && (
                <button
                  onClick={handleCancelSubscription}
                  disabled={cancelling}
                  className="text-sm text-red-400 hover:text-red-300 hover:bg-red-500/10 px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50"
                >
                  {cancelling ? 'Cancelling...' : 'Cancel Subscription'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Plans Comparison Matrix */}
      <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" style={{ tableLayout: 'fixed' }}>
            {/* Header Row with Plan Names, Prices, and Buttons */}
            <thead>
              <tr className="border-b border-slate-700">
                <th 
                  className="text-left p-2 sm:p-4 bg-slate-900"
                  style={{ width: `${featureColWidth}%` }}
                >
                  <span className="text-gray-400 text-xs sm:text-sm font-medium">Compare Plans</span>
                </th>
                {availablePlans.map(plan => {
                  const planId = plan.id || plan.tier_key || plan.tier;
                  const currentPackageId = currentSubscription?.current_package?.id || 
                                          currentSubscription?.current_package?.tier_key || 
                                          currentSubscription?.current_package?.tier || null;
                  const isCurrentPlan = planId === currentPackageId;
                  
                  // Check if this is the flash upgrade plan (Pro during flash upgrade)
                  const isFlashUpgradeActive = hasFlashUpgrade || currentSubscription?.flash_upgrade?.active;
                  const isProPlan = (plan.tier_key || plan.id || '').toLowerCase().includes('pro');
                  const isFlashUpgradePlan = isFlashUpgradeActive && isProPlan;
                  
                  // Determine column highlight: amber for flash upgrade Pro, purple for normal current plan
                  // Only highlight if there's an active PayPal subscription
                  const getColumnClass = () => {
                    if (isFlashUpgradePlan) return 'bg-amber-900/30';
                    if (isCurrentPlan && !isFlashUpgradeActive && hasActivePayPalSubscription) return 'bg-purple-900/30';
                    return 'bg-slate-900';
                  };
                  
                  return (
                    <th 
                      key={planId}
                      className={`text-center p-2 sm:p-4 ${getColumnClass()}`}
                      style={{ width: `${planColWidth}%` }}
                    >
                      <div className="flex flex-col items-center gap-1 sm:gap-2">
                        <span className="text-white font-bold text-xs sm:text-base md:text-lg uppercase">{plan.name}</span>
                        <div className="flex flex-col items-center">
                          <div className="flex items-baseline gap-0.5 sm:gap-1">
                            <span className="text-sm sm:text-xl md:text-2xl font-bold text-white">
                              £{getPlanPrice(plan)}
                            </span>
                            <span className="text-gray-400 text-xs sm:text-sm">/mo</span>
                          </div>
                          <span className="text-gray-500 text-xs">(inc. VAT)</span>
                        </div>
                        <div className="mt-1 sm:mt-2">
                          {getPlanButton(plan)}
                        </div>
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            
            {/* Feature Groups */}
            <tbody>
              {featureGroups.map((group, groupIndex) => (
                <React.Fragment key={group.name}>
                  {/* Group Header */}
                  <tr className="bg-slate-700/50">
                    <td 
                      colSpan={numPlans + 1} 
                      className="px-2 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm font-semibold text-purple-400 uppercase tracking-wider"
                    >
                      {group.name}
                    </td>
                  </tr>
                  
                  {/* Feature Rows */}
                  {group.features.map((feature, featureIndex) => (
                    <tr 
                      key={feature.key}
                      className={`border-b border-slate-700/50 ${
                        featureIndex % 2 === 0 ? 'bg-slate-800' : 'bg-slate-800/50'
                      } hover:bg-slate-700/30 transition-colors`}
                    >
                      <td 
                        className="px-2 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm text-gray-300"
                        style={{ width: `${featureColWidth}%` }}
                      >
                        {feature.label}
                      </td>
                      {availablePlans.map(plan => {
                        const planId = plan.id || plan.tier_key || plan.tier;
                        const currentPackageId = currentSubscription?.current_package?.id || 
                                                currentSubscription?.current_package?.tier_key || 
                                                currentSubscription?.current_package?.tier || null;
                        const isCurrentPlan = planId === currentPackageId;
                        
                        // Check if this is the flash upgrade plan (Pro during flash upgrade)
                        const isFlashUpgradeActive = hasFlashUpgrade || currentSubscription?.flash_upgrade?.active;
                        const isProPlan = (plan.tier_key || plan.id || '').toLowerCase().includes('pro');
                        const isFlashUpgradePlan = isFlashUpgradeActive && isProPlan;
                        
                        // Determine column highlight: amber for flash upgrade Pro, purple for normal current plan
                        // Only highlight if there's an active PayPal subscription
                        const getColumnClass = () => {
                          if (isFlashUpgradePlan) return 'bg-amber-900/10';
                          if (isCurrentPlan && !isFlashUpgradeActive && hasActivePayPalSubscription) return 'bg-purple-900/10';
                          return '';
                        };
                        
                        return (
                          <td 
                            key={planId}
                            className={`px-1 sm:px-4 py-2 sm:py-3 text-center ${getColumnClass()}`}
                            style={{ width: `${planColWidth}%` }}
                          >
                            {renderFeatureCell(plan, feature)}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Billing History & Subscription Log - Tabbed View */}
      <div className="bg-slate-800 rounded-xl border border-slate-700">
        {/* Tab Headers */}
        <div className="flex border-b border-slate-700">
          <button
            onClick={() => setActiveHistoryTab('billing')}
            className={`flex-1 px-6 py-4 text-sm font-semibold transition-colors ${
              activeHistoryTab === 'billing'
                ? 'text-white border-b-2 border-purple-500 bg-slate-700/30'
                : 'text-gray-400 hover:text-white hover:bg-slate-700/20'
            }`}
            data-testid="billing-history-tab"
          >
            <span className="flex items-center justify-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
              </svg>
              Billing History
            </span>
          </button>
          <button
            onClick={() => setActiveHistoryTab('subscription')}
            className={`flex-1 px-6 py-4 text-sm font-semibold transition-colors ${
              activeHistoryTab === 'subscription'
                ? 'text-white border-b-2 border-purple-500 bg-slate-700/30'
                : 'text-gray-400 hover:text-white hover:bg-slate-700/20'
            }`}
            data-testid="subscription-log-tab"
          >
            <span className="flex items-center justify-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
              </svg>
              Subscription Log
            </span>
          </button>
        </div>
        
        {/* Tab Content */}
        {activeHistoryTab === 'billing' ? (
          // Billing History Tab
          billingHistory && billingHistory.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full" style={{ tableLayout: 'fixed' }}>
                <thead>
                  <tr className="bg-slate-700/50">
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase" style={{ width: '70%' }}>Transaction Details</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-400 uppercase" style={{ width: '25%' }}>Document</th>
                    <th style={{ width: '5%' }}></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700">
                  {billingHistory.map((transaction, index) => (
                    <tr key={transaction.id || index} className="hover:bg-slate-700/50">
                      <td className="px-4 py-4">
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                          <span className="text-sm text-gray-400 whitespace-nowrap">
                            {transaction.date ? new Date(transaction.date).toLocaleDateString('en-GB', {
                              day: '2-digit',
                              month: 'short',
                              year: 'numeric'
                            }) : 'N/A'}
                          </span>
                          <span className="text-sm text-white">
                            {transaction.description || 'Payment'}
                          </span>
                          <span className="text-sm font-semibold text-white">
                            £{typeof transaction.amount === 'number' 
                              ? transaction.amount.toFixed(2) 
                              : typeof transaction.amount === 'object' && transaction.amount?.value
                              ? parseFloat(transaction.amount.value).toFixed(2)
                              : transaction.amount}
                          </span>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                            transaction.status === 'COMPLETED' || transaction.status === 'CAPTURED'
                              ? 'bg-green-500/20 text-green-400' 
                              : transaction.status === 'REFUNDED'
                              ? 'bg-blue-500/20 text-blue-400'
                              : 'bg-slate-700 text-gray-400'
                          }`}>
                            {transaction.status}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-4 text-center">
                        {transaction.can_download ? (
                          <button
                            onClick={async () => {
                              if (downloadingDocument === transaction.id) return;
                              setDownloadingDocument(transaction.id);
                              try {
                                const response = await fetch(`${API}/admin/billing-history/document/${transaction.id}`, {
                                  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                                });
                                if (!response.ok) {
                                  const error = await response.json();
                                  throw new Error(error.detail || 'Failed to download');
                                }
                                const blob = await response.blob();
                                const url = window.URL.createObjectURL(blob);
                                const a = document.createElement('a');
                                a.href = url;
                                a.download = `${transaction.doc_type === 'credit_note' ? 'CN' : 'INV'}-${transaction.id.slice(-8)}.pdf`;
                                document.body.appendChild(a);
                                a.click();
                                window.URL.revokeObjectURL(url);
                                a.remove();
                              } catch (error) {
                                setErrorModal({
                                  isOpen: true,
                                  title: 'Download Failed',
                                  message: error.message || 'Failed to download document'
                                });
                              } finally {
                                setDownloadingDocument(null);
                              }
                            }}
                            disabled={downloadingDocument === transaction.id}
                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-white text-xs font-medium rounded-lg transition-all duration-200 ${
                              downloadingDocument === transaction.id
                                ? 'opacity-75 cursor-wait'
                                : 'hover:scale-105 active:scale-95'
                            } ${
                              transaction.doc_type === 'credit_note'
                                ? 'bg-blue-600 hover:bg-blue-500'
                                : 'bg-purple-600 hover:bg-purple-500'
                            }`}
                            title={transaction.doc_type === 'credit_note' ? 'Download Credit Note' : 'Download Invoice'}
                          >
                            {downloadingDocument === transaction.id ? (
                              <>
                                <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Generating...
                              </>
                            ) : (
                              <>
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                {transaction.doc_type === 'credit_note' ? 'Credit Note' : 'Invoice'}
                              </>
                            )}
                          </button>
                        ) : (
                          <span className="text-xs text-gray-500">-</span>
                        )}
                      </td>
                      <td></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="px-6 py-8 text-center">
              <svg className="w-12 h-12 text-slate-600 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
              </svg>
              <p className="text-gray-400 text-sm">No billing transactions yet</p>
            </div>
          )
        ) : (
          // Subscription Log Tab
          subscriptionLog && subscriptionLog.length > 0 ? (
            <div className="p-4 space-y-2">
              {subscriptionLog.map((log, index) => (
                <div key={index} className="flex items-center gap-3 py-2 border-b border-slate-700/50 last:border-0">
                  <span className="text-sm text-gray-400 whitespace-nowrap">
                    {log.timestamp ? new Date(log.timestamp).toLocaleString('en-GB', {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    }) : 'N/A'}
                  </span>
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${
                    log.change_type?.includes('completed') ? 'bg-green-500/20 text-green-400' :
                    log.change_type?.includes('initiated') || log.change_type?.includes('started') ? 'bg-blue-500/20 text-blue-400' :
                    log.change_type?.includes('cancelled') ? 'bg-amber-500/20 text-amber-400' :
                    log.change_type?.includes('failed') ? 'bg-red-500/20 text-red-400' :
                    'bg-slate-700 text-gray-400'
                  }`}>
                    {log.change_type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="px-6 py-8 text-center">
              <svg className="w-12 h-12 text-slate-600 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
              </svg>
              <p className="text-gray-400 text-sm">No subscription activity logged yet</p>
            </div>
          )
        )}
      </div>

      {/* Modals - using shared render function */}
      {renderModals()}
    </div>
  );
};

export default AdminPlansPage;
